import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import {
  Close as CloseIcon,
  Undo as UndoIcon,
  DeleteOutline as ClearIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import RoiEditor, {
  type RoiPoint,
  type RoiOverlay,
  MAX_ROI_POINTS,
} from "./RoiEditor"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import { streamService, type SnapshotParams } from "../services/streamService"
import api from "../services/api"

/** As três regiões desenhadas sobre o mesmo quadro. */
export interface RoiRegions {
  /** Baia — a região de análise principal (`cameraRoi`). */
  roi: RoiPoint[]
  /** Bebedouro (`cameraRoiWater`). */
  water: RoiPoint[]
  /** Comedouro (`cameraRoiFeed`). */
  feed: RoiPoint[]
}

type Camada = keyof RoiRegions

/**
 * Água e comida são áreas pequenas e bem delimitadas — um bebedouro não
 * precisa de vinte vértices. O teto menor também mantém as colunas novas
 * curtas, na mesma lógica do limite da `cameraRoi`.
 */
export const MAX_SUB_ROI_POINTS = 8

const CORES: Record<Camada, string> = {
  roi: "#22c55e", // verde — baia
  water: "#38bdf8", // azul — água
  feed: "#f59e0b", // âmbar — comida
}

const TETOS: Record<Camada, number> = {
  roi: MAX_ROI_POINTS,
  water: MAX_SUB_ROI_POINTS,
  feed: MAX_SUB_ROI_POINTS,
}

interface RoiEditorDialogProps {
  open: boolean
  onClose: () => void
  onSave: (regions: RoiRegions) => void
  tenantId: number
  recorderId: number
  channel: number
  cameraName?: string
  initialPoints?: RoiPoint[]
  initialWater?: RoiPoint[]
  initialFeed?: RoiPoint[]
}

const RoiEditorDialog: React.FC<RoiEditorDialogProps> = ({
  open,
  onClose,
  onSave,
  tenantId,
  recorderId,
  channel,
  cameraName,
  initialPoints = [],
  initialWater = [],
  initialFeed = [],
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  // Em telas menores vira tela cheia; no desktop, um diálogo alto (92vh). Em
  // ambos a imagem é dimensionada para caber inteira, sem scroll.
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))
  const [regions, setRegions] = useState<RoiRegions>({
    roi: initialPoints,
    water: initialWater,
    feed: initialFeed,
  })
  const [camada, setCamada] = useState<Camada>("roi")

  // A camada ativa é a única editável; as outras entram como referência.
  const points = regions[camada]
  const maxPoints = TETOS[camada]
  const cor = CORES[camada]
  const setPoints = useCallback(
    (proximos: RoiPoint[] | ((p: RoiPoint[]) => RoiPoint[])) => {
      setRegions((atual) => ({
        ...atual,
        [camada]:
          typeof proximos === "function" ? proximos(atual[camada]) : proximos,
      }))
    },
    [camada],
  )

  const overlays: RoiOverlay[] = (Object.keys(CORES) as Camada[])
    .filter((c) => c !== camada && regions[c].length >= 2)
    .map((c) => ({ points: regions[c], color: CORES[c] }))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null)
  const [aspectRatio, setAspectRatio] = useState(16 / 9)
  const snapshotParamsRef = useRef<SnapshotParams | null>(null)
  const prevUrlRef = useRef<string | null>(null)

  const fetchSnapshot = async (params: SnapshotParams) => {
    setLoading(true)
    setError(null)
    try {
      const url = await streamService.getSnapshot(params)
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current)
      prevUrlRef.current = url
      setSnapshotUrl(url)
    } catch (e: any) {
      setError(t("stream.frameError"))
      console.error("[snapshot]", e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return
    setRegions({ roi: initialPoints, water: initialWater, feed: initialFeed })
    setCamada("roi")
    setError(null)
    setSnapshotUrl(null)
    snapshotParamsRef.current = null

    const load = async () => {
      setLoading(true)
      try {
        const resp = await api.get(`/recorder/${tenantId}/${recorderId}`)
        const rec = resp.data?.recorder || resp.data
        if (!rec) throw new Error(t("stream.recorderNotFound"))

        const params: SnapshotParams = {
          rtspUser: rec.recorderUser || rec.recorder_rtsp_user,
          rtspPass: rec.recorderPass || rec.recorder_rtsp_pass,
          recorderExtIP: rec.recorderExtIP || rec.recorder_ext_ip,
          recorderPort: rec.recorderExtRTSP ? String(rec.recorderExtRTSP) : "554",
          channel,
          recorderTypeName: String(rec.recorderTypeName || rec.recorder_type_name || "Hikvision"),
        }

        if (!params.rtspUser || !params.rtspPass) throw new Error(t("stream.rtspCredentialsNotFound"))
        if (!params.recorderExtIP || params.recorderExtIP === "127.0.0.1")
          throw new Error(t("stream.recorderExtIpNotConfigured"))

        snapshotParamsRef.current = params
        await fetchSnapshot(params)
      } catch (e: any) {
        setError(e.message || t("stream.cameraConnectError"))
        setLoading(false)
      }
    }

    load()

    return () => {
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current)
        prevUrlRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recorderId, channel, tenantId])

  const handleRefresh = () => {
    if (snapshotParamsRef.current) fetchSnapshot(snapshotParamsRef.current)
  }

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (img.naturalWidth && img.naturalHeight) {
      setAspectRatio(img.naturalWidth / img.naturalHeight)
    }
  }

  const handleUndo = () => setPoints((prev) => prev.slice(0, -1))
  const handleClear = () => setPoints([])
  const handleSave = () => {
    onSave(regions)
    onClose()
  }

  /** Região válida = vazia (não definida) ou polígono fechado. */
  const invalida = (p: RoiPoint[]) => p.length !== 0 && p.length < 3
  const algumaInvalida =
    invalida(regions.roi) || invalida(regions.water) || invalida(regions.feed)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { height: fullScreen ? "100%" : "92vh", maxHeight: "100%" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{t("stream.roiTitle")}{cameraName ? ` — ${cameraName}` : ""}</span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", overflow: "hidden", gap: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("stream.roiInstructions")}
        </Typography>

        {error && (
          <Alert severity="warning" sx={{ py: 0 }}>
            {error} {t("stream.frameErrorSuffix")}
          </Alert>
        )}

        {/* Qual região está sendo desenhada. As outras duas aparecem no quadro
            em tracejado, para não se desenhar às cegas sobre elas. */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={camada}
          onChange={(_, v) => v && setCamada(v as Camada)}
          sx={{ alignSelf: "flex-start", flexWrap: "wrap" }}
        >
          {(["roi", "water", "feed"] as Camada[]).map((c) => (
            <ToggleButton key={c} value={c} sx={{ gap: 0.75, px: 1.5 }}>
              <Box
                component="span"
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  bgcolor: CORES[c],
                  flexShrink: 0,
                }}
              />
              {t(`stream.layer_${c}`)}
              <Box component="span" sx={{ opacity: 0.6, fontSize: 11 }}>
                {regions[c].length > 0 ? `(${regions[c].length})` : ""}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {points.length >= maxPoints && (
          <Alert severity="info" sx={{ py: 0 }}>
            {t("stream.maxPointsReached", { max: maxPoints })}
          </Alert>
        )}

        {/* Área da imagem: ocupa o espaço livre e dimensiona o quadro para caber
            INTEIRO (sem scroll). Limitamos a largura por (altura disponível ×
            proporção) para a altura derivada nunca passar da área — preservando
            a proporção exata, essencial para o overlay do ROI ficar alinhado. */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: `calc((${fullScreen ? "100vh" : "92vh"} - 300px) * ${aspectRatio})`,
            }}
          >
            <RoiEditor
              points={points}
              onChange={setPoints}
              aspectRatio={aspectRatio}
              maxPoints={maxPoints}
              color={cor}
              overlays={overlays}
            >
              {snapshotUrl ? (
                <img
                  src={snapshotUrl}
                  onLoad={handleImageLoad}
                  alt={t("stream.cameraFrame")}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    background: "#000",
                    display: "block",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: error
                      ? "repeating-linear-gradient(45deg,#111,#111 10px,#1a1a1a 10px,#1a1a1a 20px)"
                      : "#000",
                  }}
                />
              )}
            </RoiEditor>

            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={28} />
                <Typography variant="caption">{t("stream.capturing")}</Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Tooltip title={t("stream.newFrame")}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading || !snapshotParamsRef.current}
              >
                {t("stream.refreshFrame")}
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UndoIcon />}
            onClick={handleUndo}
            disabled={points.length === 0}
          >
            {t("stream.undoPoint")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            disabled={points.length === 0}
          >
            {t("stream.clear")}
          </Button>
          <Typography
            variant="caption"
            color={points.length >= maxPoints ? "warning.main" : "text.secondary"}
            sx={{ ml: "auto" }}
          >
            {t("stream.pointsCount", { n: points.length, max: maxPoints })}
            {points.length > 0 && points.length < 3 ? ` ${t("stream.minPointsHint")}` : ""}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          // Qualquer uma das três com 1 ou 2 pontos é polígono inacabado —
          // salvar assim gravaria lixo que a IA não sabe usar.
          disabled={algumaInvalida}
        >
          {t("stream.saveRegion")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RoiEditorDialog
